import {
	Anchor,
	Button,
	Group,
	List,
	Paper,
	RingProgress,
	Stack,
	Text,
	Title,
	Flex,
	Box,
	useMantineColorScheme,
	useMantineTheme
} from "@mantine/core"

import styles from "./TrainingCard.module.css"
import {
	TrainingCardItem,
	TrainingLink
} from "@pages/home/trainingAndCertification/TrainingAndCertification"
import { useRedirect } from "@hooks/useRedirect"
import { Brands } from "@utils/constants.util"
import { AEMSvg } from "@components/AEMSvg"
import { eventTrackingItems } from "@utils/eventTracking"

export function TrainingCard(card: TrainingCardItem) {
	const { handleRedirect } = useRedirect()
	const mode = card.mode
	const progressPercent = card.progressPercent ?? 0
	const gap = mode === "full" ? "xl" : "xs"
	const listSpacing = mode === "full" ? "xs" : 0
	const fontSize = mode === "full" ? 16 : 14
	const buttonSize = mode === "full" ? "lg" : "sm"
	const minWidthSize = mode === "full" ? 392 : 330
	const isSilversea = card.brand?.brandCode === Brands.SILVERSEA

	const trainingCardclick = (card: TrainingLink) => {
		eventTrackingItems({
			event_name: "training_click",
			category: "Training Click",
			training_type: card?.type,
			clickText: card.label,
			link_url: card?.link || "",
			event211: 1
		})
	}

	const { colorScheme } = useMantineColorScheme()
	const theme = useMantineTheme()

	return (
		<Paper
			data-variant={mode}
			className={styles["training-card"]}
			miw={minWidthSize}
			w={"100%"}
		>
			<Flex
				direction="column"
				gap={{ base: "xs", sm: gap }}
				align="flex-start"
				justify={"center"}
			>
				<AEMSvg
					mb={isSilversea ? 18 : 0}
					mih={isSilversea ? 22 : 44}
					svgData={{
						path: card.brand?.logo?.path,
						iconColor: colorScheme === "dark" ? theme.colors.royal[6] : ""
					}}
					w={{ base: 133, sm: 188 }}
					ariaLabel={`${card.platformTitle} Icon`}
				/>
				<Group wrap={"nowrap"} className={styles["training-gauge"]}>
					{/* TODO: this shouldn't be this way, but Silverses is the only brand that is currently not using percentage complete */}
					{card.brand?.brandCode !== "V" && (
						<RingProgress
							size={80}
							thickness={6}
							sections={[
								{ value: progressPercent, color: "var(--text-color-ternary)" }
							]}
							label={
								<Text className={styles["progress-label"]}>
									{progressPercent}%
								</Text>
							}
						/>
					)}

					<Stack gap={0} align="flex-start">
						{card.platformTitle && (
							<Title
								order={3}
								fz={{ base: 14, sm: fontSize }}
								lh={1.3}
								fw={600}
								className={styles.trainingCardText}
							>
								{card.platformTitle}
							</Title>
						)}
						{card.platformLink && (
							<Anchor
								component="button"
								fw={600}
								fz={{ base: 11, sm: 12 }}
								underline="always"
								onClick={() => {
									if (card.platformLink) {
										trainingCardclick(card.platformLink)
										handleRedirect({
											link: card.platformLink?.link,
											type: card.platformLink?.type,
											appId: card.platformLink?.appId ?? undefined
										})
									}
								}}
								className={styles.trainingCardText}
								data-qa={`secure.training.${card?.brand?.brandCode}.button.continue`}
							>
								{card.platformLink.label}
							</Anchor>
						)}
					</Stack>
				</Group>
				{card.trainingHeader && card.trainingLinks && (
					<Box>
						<Title
							order={3}
							fz={{ base: 14, sm: fontSize }}
							fw={600}
							className={styles.trainingCardText}
							mb="xs"
						>
							{card.trainingHeader}
						</Title>
						<List spacing={listSpacing}>
							{card.trainingLinks.map((item, index) => (
								<List.Item key={index}>
									<Anchor
										component="button"
										fw={500}
										fz={{ base: 13, sm: fontSize }}
										underline="always"
										onClick={() => {
											trainingCardclick(item)
											handleRedirect({
												link: item?.link,
												type: item?.type,
												appId: item?.appId ?? undefined
											})
										}}
										className={styles.trainingCardText}
										data-qa={`secure.training.${card?.brand?.brandCode}.link.${index}`}
									>
										{item.label}
									</Anchor>
								</List.Item>
							))}
						</List>
					</Box>
				)}
			</Flex>
			{card.primaryLink && (
				<Group wrap="nowrap" mt={30}>
					<Button
						size={buttonSize}
						onClick={() => {
							if (card.primaryLink) {
								trainingCardclick(card.primaryLink)
								handleRedirect({
									link: card.primaryLink.link,
									type: card.primaryLink.type,
									appId: card.primaryLink.appId ?? undefined
								})
							}
						}}
						fw={700}
						fz={{ base: 14, sm: fontSize }}
						variant={"white"}
						aria-label={`${card.primaryLink.label} training for ${card.platformTitle}`}
						data-qa={`secure.training.${card?.brand?.brandCode}.button.explore`}
					>
						{card.primaryLink.label}
					</Button>
					{card.secondaryLink && (
						<Anchor
							component="button"
							onClick={() => {
								if (card.secondaryLink) {
									trainingCardclick(card.secondaryLink)
									handleRedirect({
										link: card.secondaryLink?.link,
										type: card.secondaryLink?.type,
										appId: card.secondaryLink?.appId ?? undefined
									})
								}
							}}
							fz={{ base: 12, sm: 13 }}
							className={styles.trainingCardText}
							fw={700}
							lh={1.6}
							tt="uppercase"
							underline="hover"
							variant={"transparent"}
							data-qa={`secure.training.${card?.brand?.brandCode}.button.webinars`}
						>
							{card.secondaryLink.label}
						</Anchor>
					)}
				</Group>
			)}
		</Paper>
	)
}
